import type TileSource from 'ol/source/Tile.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';

interface SatelliteTileSource {
  source: Pick<TileSource, 'on' | 'un'>;
  layer: 'imagery' | 'labels';
}

interface SatelliteTileMonitorOptions {
  basemap: 'satellite' | 'hybrid';
  recover?: () => Promise<void>;
  reportFailure?: boolean;
}

export function monitorSatelliteTiles(
  sources: SatelliteTileSource[],
  track: MapAnalyticsTrack,
  options: SatelliteTileMonitorOptions,
): () => void {
  let recovery: 'available' | 'recovering' | 'attempted' | 'stopped' = 'available';

  const monitors = sources.map(({ source, layer }) => {
    let started = 0, loaded = 0, failed = 0;
    let consecutiveFailures = 0;
    let failureReported = false;
    const reportFailure = () => {
      if (recovery === 'stopped' || options.reportFailure === false || failureReported || consecutiveFailures < 3) return;
      failureReported = true;
      track('satellite_tile_load_failed', {
        basemap: options.basemap, tile_layer: layer,
        tiles_started: String(started), tiles_failed: String(failed),
        error_blob: JSON.stringify({
          kind: 'tile_load_error', message: 'Satellite tiles failed to load',
          source: layer === 'labels' ? 'azure.base_map_tiles' : 'azure.imagery_tiles',
          page_path: !('location' in globalThis) ? '/' : location.pathname,
        }),
      });
    };
    const onStart = () => { started++; };
    const onLoad = () => {
      loaded++;
      consecutiveFailures = 0;
      if (recovery !== 'recovering' && recovery !== 'stopped') recovery = 'available';
    };
    const onError = () => {
      if (recovery === 'stopped') return;
      failed++;
      consecutiveFailures++;
      if (options.recover && recovery === 'available') {
        recovery = 'recovering';
        void options.recover()
          .catch(() => { reportFailure(); })
          .finally(() => { if (recovery === 'recovering') recovery = 'attempted'; });
        return;
      }
      if (recovery !== 'recovering') reportFailure();
    };
    source.on('tileloadstart', onStart);
    source.on('tileloadend', onLoad);
    source.on('tileloaderror', onError);
    return {
      flush() {
        if (started + loaded + failed === 0) return;
        track('satellite_tile_session', {
          basemap: options.basemap, tile_layer: layer,
          tiles_started: String(started), tiles_loaded: String(loaded), tiles_failed: String(failed),
        });
        started = 0; loaded = 0; failed = 0;
      },
      dispose() {
        source.un('tileloadstart', onStart);
        source.un('tileloadend', onLoad);
        source.un('tileloaderror', onError);
      },
    };
  });
  const flush = () => { monitors.forEach(monitor => monitor.flush()); };
  const hidden = () => { if (document.visibilityState === 'hidden') flush(); };
  // Deltas avoid double counting when a periodic flush is followed by pagehide.
  const timer = setInterval(flush, 60_000);
  if ('window' in globalThis) window.addEventListener('pagehide', flush, { capture: true });
  if ('document' in globalThis) document.addEventListener('visibilitychange', hidden, true);
  return () => {
    if (recovery === 'stopped') return;
    recovery = 'stopped';
    clearInterval(timer);
    monitors.forEach(monitor => monitor.dispose());
    if ('window' in globalThis) window.removeEventListener('pagehide', flush, true);
    if ('document' in globalThis) document.removeEventListener('visibilitychange', hidden, true);
    flush();
  };
}
